"use client"

/**
 * /actions — section 01 "Evidence" interactive panel.
 *
 * Ported from Actions.dc.html (the `data-e="tabs"` / `data-e="rows"` block and
 * its EV data + paintEv logic). Five evidence types (Questions, AI answers,
 * Competitors, Sources, Website); the active one drives the lead line and the
 * row list. Auto-cycles every 5s until the visitor picks a tab; the active
 * tab carries a filling progress hairline. Reduced-motion disables the cycle.
 */

import { useEffect, useState } from "react"

const MONO = "var(--vx-mono)"

const ST: Record<string, string> = {
  Absent: "#E4CBA2",
  Missing: "#E4CBA2",
  Gap: "#E4CBA2",
  Ahead: "#E4CBA2",
  "No mention": "#E4CBA2",
  Outdated: "#E4CBA2",
  Mentioned: "rgba(232,238,244,.75)",
  Medium: "rgba(232,238,244,.75)",
  Tracked: "#6BD5EC",
  Owned: "#8FD9C4",
}

interface EvRow {
  t: string
  m: string
  s: string
  d?: string
}
interface EvTab {
  k: string
  lead: string
  rows: EvRow[]
}

const EV: EvTab[] = [
  {
    k: "Questions",
    lead: "The tracked buyer questions where this gap keeps appearing.",
    rows: [
      { t: "Best noise-cancelling headphones for long flights", m: "4 surfaces", s: "Absent" },
      { t: "Quietest headphones for aeroplane noise", m: "4 surfaces", s: "Absent" },
      { t: "Best travel headphones for battery life", m: "3 surfaces", s: "Mentioned" },
      { t: "Comfortable over-ears for ten-hour flights", m: "3 surfaces", s: "Absent" },
    ],
  },
  {
    k: "AI answers",
    lead: "The captured responses behind the Action, on each surface.",
    rows: [
      { d: "openai.com", t: "ChatGPT · “Sony, Bose and AirPods Max come up again and again.”", m: "12 Mar", s: "Absent" },
      { d: "gemini.google.com", t: "Gemini · “Most reviewers converge on Bose and Sony.”", m: "12 Mar", s: "Mentioned" },
      { d: "perplexity.ai", t: "Perplexity · “Measured isolation favours Sony, then Apple.”", m: "11 Mar", s: "Absent" },
      { d: "claude.ai", t: "Claude · “Bose for comfort, Sony for raw reduction.”", m: "11 Mar", s: "Absent" },
    ],
  },
  {
    k: "Competitors",
    lead: "Brands recommended ahead of Sennheiser across the same questions.",
    rows: [
      { d: "sony.com", t: "Sony", m: "Named in 18 of 24", s: "Ahead" },
      { d: "bose.com", t: "Bose", m: "Named in 14 of 24", s: "Ahead" },
      { d: "apple.com", t: "Apple", m: "Named in 10 of 24", s: "Ahead" },
      { d: "sennheiser.com", t: "Sennheiser", m: "Named in 12 of 24", s: "Tracked" },
    ],
  },
  {
    k: "Sources",
    lead: "Domains cited when these questions are answered, and where you are missing.",
    rows: [
      { d: "rtings.com", t: "rtings.com · measured ANC tests", m: "Cited 19×", s: "No mention" },
      { d: "theverge.com", t: "theverge.com · travel headphone roundup", m: "Cited 14×", s: "No mention" },
      { d: "reddit.com", t: "reddit.com/r/headphones · flight threads", m: "Cited 9×", s: "Gap" },
      { d: "sennheiser.com", t: "sennheiser.com · product pages", m: "Cited 2×", s: "Owned" },
    ],
  },
  {
    k: "Website",
    lead: "Page and content findings that help explain the absence.",
    rows: [
      { t: "No comparison page for travel headphones", m: "Content", s: "Missing" },
      { t: "Noise-cancelling claims have no test data", m: "Proof", s: "Missing" },
      { t: "Battery-life answer buried below the fold", m: "Structure", s: "Medium" },
      { t: "Product schema missing on two pages", m: "Technical", s: "Missing" },
    ],
  },
]

function fav(d: string) {
  return `https://www.google.com/s2/favicons?sz=64&domain=${d}`
}

export function EvidencePanel() {
  const [active, setActive] = useState(0)
  const [paused, setPaused] = useState(false)
  const [reduced, setReduced] = useState(false)

  useEffect(() => {
    if (typeof window === "undefined") return
    setReduced(window.matchMedia("(prefers-reduced-motion: reduce)").matches)
  }, [])

  useEffect(() => {
    if (paused || reduced) return
    const id = setInterval(() => setActive((a) => (a + 1) % EV.length), 5000)
    return () => clearInterval(id)
  }, [paused, reduced])

  const tab = EV[active]

  return (
    <div
      data-a="panel"
      className="vx-ev-panel"
      style={{
        position: "relative",
        boxSizing: "border-box",
        marginTop: 22,
        borderRadius: 20,
        border: "1px solid rgba(160,196,224,.16)",
        background: "linear-gradient(168deg, #070B11, #030509 74%)",
        overflow: "hidden",
      }}
    >
      <div
        className="vx-ev-head"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
          padding: "18px 24px",
          borderBottom: "1px solid rgba(160,196,224,.1)",
        }}
      >
        <div
          style={{
            flex: "1 1 320px",
            minWidth: 0,
            fontSize: 13.5,
            lineHeight: 1.6,
            color: "rgba(232,238,244,.66)",
          }}
        >
          {tab.lead}
        </div>
        <div role="tablist" aria-label="Evidence type" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {EV.map((e, i) => {
            const on = i === active
            return (
              <button
                key={e.k}
                type="button"
                data-e="tab"
                role="tab"
                aria-selected={on}
                onClick={() => {
                  setPaused(true)
                  setActive(i)
                }}
                style={{
                  position: "relative",
                  margin: 0,
                  padding: "7px 11px",
                  borderRadius: 8,
                  border: `1px solid ${on ? "rgba(107,213,236,.45)" : "rgba(160,196,224,.14)"}`,
                  background: on ? "rgba(107,213,236,.1)" : "transparent",
                  font: `500 9px/1 ${MONO}`,
                  letterSpacing: ".12em",
                  textTransform: "uppercase",
                  color: on ? "#DEF6FC" : "rgba(232,238,244,.62)",
                  overflow: "hidden",
                  cursor: "pointer",
                }}
              >
                {e.k}
                {on && !paused && !reduced ? (
                  <span
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      left: 0,
                      bottom: 0,
                      height: 2,
                      width: 0,
                      background: "#6BD5EC",
                      animation: "vxaProg 4.8s linear forwards",
                    }}
                  />
                ) : null}
              </button>
            )
          })}
        </div>
      </div>

      <div
        key={active}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          padding: "18px 24px 22px",
          minHeight: 208,
        }}
      >
        {tab.rows.map((r, i) => {
          const c = ST[r.s] || "rgba(232,238,244,.7)"
          const badgeBorder = c === "rgba(232,238,244,.75)" ? "rgba(160,196,224,.22)" : c
          return (
            <div
              key={i}
              data-a="row"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "13px 13px",
                borderRadius: 10,
                border: "1px solid rgba(160,196,224,.12)",
                animation: reduced ? "none" : "vxaRowIn .5s cubic-bezier(.2,.7,.2,1) both",
                animationDelay: reduced ? undefined : `${i * 80}ms`,
              }}
            >
              {r.d ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={fav(r.d)}
                  alt=""
                  width={18}
                  height={18}
                  style={{ flex: "none", display: "block", borderRadius: 5 }}
                />
              ) : (
                <span
                  aria-hidden="true"
                  style={{ flex: "none", width: 9, height: 9, borderRadius: "50%", border: `1.5px solid ${c}` }}
                />
              )}
              <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, lineHeight: 1.4, color: "rgba(240,245,249,.9)" }}>
                {r.t}
              </span>
              <span
                style={{
                  flex: "none",
                  font: `400 9.5px/1 ${MONO}`,
                  letterSpacing: ".08em",
                  textTransform: "uppercase",
                  color: "rgba(232,238,244,.5)",
                  whiteSpace: "nowrap",
                }}
              >
                {r.m}
              </span>
              <span
                style={{
                  flex: "none",
                  font: `500 8.5px/1 ${MONO}`,
                  letterSpacing: ".12em",
                  textTransform: "uppercase",
                  color: c,
                  border: `1px solid ${badgeBorder}`,
                  padding: "5px 7px",
                  borderRadius: 6,
                  whiteSpace: "nowrap",
                }}
              >
                {r.s}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
